import { computed, DestroyRef, inject } from '@angular/core';
import { patchState, signalStore, withComputed, withHooks, withMethods, withState } from '@ngrx/signals';
import { Subject, switchMap, takeUntil, tap } from 'rxjs';
import { latestRequest, RequestStatus } from '../../state/latest-request';
import { scopedRequest } from '../../state/scoped-request';
import { SessionStateResetService } from '../../state/session-state-reset.service';
import { IsConnectedStatus, QuickbooksService, QuickbookStatus } from './quickbooks.service';

const initialState = () => ({
  data: { currentStatus: IsConnectedStatus.disconnected, email: '' } as QuickbookStatus,
  requests: {} as Record<string, RequestStatus>,
});

// Connection state is shared by the app banner and QBO screen. Reset on logout;
// reload on layout entry and replace from server responses after every mutation.
export const QuickbooksStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withComputed((store) => ({
    connectionStatus: computed(() => store.data.currentStatus()),
    tableData: computed(() => {
      const data = store.data();
      return [
        ...(data.connectedEvent ?? []),
        ...(data.disconnectedEvent ?? []),
        ...(data.syncedEvent ?? []),
        ...(data.syncFailedEvent ?? []),
      ].sort((a, b) => {
        const first = new Date(a.date).getTime();
        const second = new Date(b.date).getTime();
        if (Number.isNaN(first)) return Number.isNaN(second) ? 0 : 1;
        if (Number.isNaN(second)) return -1;
        return second - first;
      });
    }),
    busy: computed(() => Object.values(store.requests()).some((request) => request.loading)),
  })),
  withMethods((store, api = inject(QuickbooksService), destroyRef = inject(DestroyRef)) => {
    const reset$ = new Subject<void>();
    const status = (key: string) => (request: RequestStatus) =>
      patchState(store, { requests: { ...store.requests(), [key]: request } });
    const load = latestRequest({
      request: (_: void) => api.status().pipe(takeUntil(reset$)),
      success: (data) => patchState(store, { data }),
      status: status('load'),
      onError: () => patchState(store, { data: initialState().data }),
      errorMessage: 'Unable to load QuickBooks status.',
    });
    return {
      load,
      hydrate(data: QuickbookStatus): void {
        patchState(store, { data });
      },
      reset(): void {
        reset$.next();
        patchState(store, initialState());
      },
      sync: scopedRequest(
        destroyRef,
        () =>
          api.sync().pipe(
            takeUntil(reset$),
            tap((data) => patchState(store, { data })),
          ),
        status('sync'),
      ),
      disconnect: scopedRequest(
        destroyRef,
        () =>
          api.disconnect().pipe(
            switchMap(() => api.status()),
            takeUntil(reset$),
            tap((data) => patchState(store, { data })),
          ),
        status('disconnect'),
      ),
      connect: scopedRequest(destroyRef, () => api.qbConnect().pipe(takeUntil(reset$)), status('connect')),
      completeConnection: scopedRequest(
        destroyRef,
        (params: Record<string, string>) => api.sendParams(params).pipe(takeUntil(reset$)),
        status('callback'),
      ),
    };
  }),
  withHooks({
    onInit(store) {
      const unregister = inject(SessionStateResetService).register(() => store.reset());
      inject(DestroyRef).onDestroy(unregister);
    },
  }),
);

export const QuickbooksPageStore = signalStore(
  withState({ currentPage: 0, pageSize: 50 }),
  withComputed((store, connection = inject(QuickbooksStore)) => ({
    pagedTableData: computed(() =>
      connection
        .tableData()
        .slice(store.currentPage() * store.pageSize(), (store.currentPage() + 1) * store.pageSize()),
    ),
  })),
  withMethods((store) => ({
    setPage(currentPage: number): void {
      patchState(store, { currentPage });
    },
  })),
);
